import { Play, ArrowRight, Lock, GitBranch, ChevronRight, Pencil, Eye } from "lucide-react";
import { canEditWorkflows } from "../../services/authService";
import WorkflowCardMenu from "./WorkflowCardMenu";

/**
 * One workflow row. Renders the real WorkflowSummary from the API.
 * Provider-bundled workflows the org isn't connected to are "gated":
 * shown with an "Available with <provider>" lock + a Connect CTA instead of Run.
 */
export default function WorkflowCard({
  workflow,
  onRun,
  onConnect,
  onOpen,
  onClone,
  onExport,
  busy = false
}) {
  const gated = workflow.is_accessible === false;
  const providerName = workflow.provider_name || "this provider";
  const flow = workflow.integration_flow || [];
  const canEdit = canEditWorkflows();

  return (
    <div
      className={`rounded-2xl border border-gray-200 p-6 transition-all ${
        gated ? "bg-gray-50/60" : "bg-white hover:shadow-md"
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-xl font-bold text-gray-900">{workflow.name}</h3>
          {gated ? (
            <span className="flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium bg-amber-100 text-amber-700">
              <Lock className="w-3 h-3" />
              Available with {providerName}
            </span>
          ) : (
            <span className="flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium bg-green-100 text-green-700">
              <Play className="w-3 h-3 fill-current" />
              Ready
            </span>
          )}
          {workflow.visibility === "provider" && workflow.provider_name && (
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
              {workflow.provider_name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {gated ? (
            <button
              onClick={() => onConnect?.(workflow)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Connect {providerName}
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <>
              <button
                onClick={() => onOpen?.(workflow)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                title={canEdit ? "Open & edit" : "View workflow"}
              >
                {canEdit ? <Pencil className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {canEdit ? "Edit" : "View"}
              </button>
              <button
                onClick={() => onRun?.(workflow)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Play className="w-4 h-4 fill-current" />
                Run
              </button>
              {/* Clone/Export. Gated workflows are excluded above: the server
                  refuses to clone what this org can't run, or to export a
                  provider-bundled workflow out of a tenant. */}
              <WorkflowCardMenu
                workflow={workflow}
                onClone={canEdit ? onClone : undefined}
                onExport={onExport}
                busy={busy}
              />
            </>
          )}
        </div>
      </div>

      {/* Description */}
      {workflow.description && (
        <p className="text-gray-600 text-sm mb-4">{workflow.description}</p>
      )}

      {/* Integration flow */}
      {flow.length > 0 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="text-sm font-semibold text-gray-700">
            Integration flow:
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            {flow.map((step, index) => (
              <div key={`${step.provider_id}-${index}`} className="flex items-center gap-2">
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-sm font-medium text-gray-700">
                  <GitBranch className="w-3.5 h-3.5" />
                  {step.provider_name}
                </span>
                {index < flow.length - 1 && (
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer: gated explainer or version */}
      {gated ? (
        <p className="text-sm text-amber-700/90">
          {workflow.access_reason ||
            `Included with ${providerName}. Connect ${providerName} to run this workflow.`}
        </p>
      ) : (
        <div className="text-sm text-gray-500">
          Version <span className="font-semibold text-gray-700">v{workflow.latest_version}</span>
        </div>
      )}
    </div>
  );
}
